import classNames from 'classnames';
import React, { ThHTMLAttributes } from 'react';
import { Stack } from '../stack';
import { TableSortIndicator, TableSortIndicatorProps } from '../table/TableSortIndicator';
import { ColumnAlign } from '../table/types';
import { StyledTableCol } from './styles';

interface TableColProps extends ThHTMLAttributes<HTMLTableCellElement> {
  id?: string;
  width?: number;
  fixed?: boolean;
  fixedColWidth?: number;
  stickyHeader?: boolean;
  groupColumn?: boolean;
  sortable?: boolean;
  resize?: boolean;
  align?: ColumnAlign;
  /**
   * 테이블 컬럼의 수직 정렬 방향
   *
   * - middle: 세로 가운데 정렬 (기본값)
   * - top: 상단 정렬
   * - bottom: 하단 정렬
   * - baseline: 텍스트 기준선 정렬
   *
   * @default middle
   */
  valign?: 'middle' | 'top' | 'bottom' | 'baseline';
  /**
   * 테이블 정렬 인디케이터 Props
   *
   * sortable이 true일 때 TableSortIndicator 컴포넌트에 전달되는 props입니다.
   * isSorted, direction 등의 정렬 상태 정보를 포함합니다.
   *
   * @default undefined
   */
  sortProps?: TableSortIndicatorProps;
  showRightBorder?: boolean;
  onClick?: () => void;
}

export const TableCol = React.forwardRef<HTMLTableCellElement, TableColProps>(
  (
    {
      children,
      className,
      onClick,
      valign = 'middle',
      sortProps,
      width,
      id,
      showRightBorder,
      resize,
      ...props
    }: TableColProps,
    ref,
  ) => {
    if (props.fixed && !width) throw new Error('fixed 테이블 컬럼은 `width` 속성이 존재해야합니다.');
    const resizable = resize && !width;

    return (
      <StyledTableCol
        scope='col'
        className={classNames(
          id ? `pds-table-cell-${id}` : 'pds-table-cell',
          props.fixed && 'pds-table-cell-fixed',
          showRightBorder && 'show-right-border',
          className,
        )}
        resize={resizable}
        {...props}
        onClick={() => onClick?.()}
        ref={ref}
        valign={valign}
      >
        <Stack
          direction='row'
          align='center'
          style={{
            justifyContent: props.align === 'center' ? 'center' : props.align === 'right' ? 'flex-end' : 'initial',
          }}
          justify='space-between'
        >
          {resizable ? <span className='ContentsWrapper'>{children}</span> : children}
          {/* @ts-expect-error: TS2322 */}
          {props.sortable && <TableSortIndicator {...sortProps} isSorted={Boolean(sortProps?.isSorted)} />}
        </Stack>
      </StyledTableCol>
    );
  },
);
